import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiSend, FiAlertCircle, FiCornerUpLeft } from 'react-icons/fi';
import { Button } from '../../components/ui/Button';
import { Label } from '../../components/ui/Label';
import { Alert } from '../../components/ui/Alert';
import { Spinner } from '../../components/ui/Spinner';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { PainelResultado } from '../../components/layout/PainelResultado';
import { useAuth } from '../../contexts/AuthContext';
import { solicitacoesService } from '../../services/solicitacoes.service';
import { esicPortalService } from '../../services/esic.service';

const INSTANCIA_DESCRICAO: Record<number, { nome: string; descricao: string }> = {
  1: { nome: '1ª instância', descricao: 'Recurso à autoridade hierarquicamente superior àquela que negou.' },
  2: { nome: '2ª instância', descricao: 'Recurso à autoridade máxima do órgão (Prefeito/Secretário).' },
  3: { nome: '3ª instância', descricao: 'Recurso à instância de controle interno municipal (Controladoria-Geral ou equivalente).' },
  4: { nome: '4ª instância', descricao: 'Última instância recursal disponível.' },
};

export function EsicRecorrerPage() {
  const { numero } = useParams<{ numero: string }>();
  const { cidadao } = useAuth();
  const navigate = useNavigate();

  const [doc, setDoc] = useState<{ id: string; numero: string; titulo: string; status: string; campos_customizados: Record<string, unknown> } | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [justificativa, setJustificativa] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [sucesso, setSucesso] = useState<{ numero: string; instancia: number } | null>(null);

  useEffect(() => {
    if (!cidadao || !numero) return;
    solicitacoesService.buscarPorNumero(cidadao.id, numero)
      .then((d) => {
        if (!d) {
          setErro('Pedido não encontrado.');
          return;
        }
        setDoc(d as never);
      })
      .catch((e) => setErro(e instanceof Error ? e.message : 'Erro'))
      .finally(() => setCarregando(false));
  }, [cidadao, numero]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!doc) return;
    if (justificativa.trim().length < 10) {
      setErro('A justificativa do recurso é obrigatória (mínimo 10 caracteres).');
      return;
    }
    setErro(null);
    setEnviando(true);
    try {
      const r = await esicPortalService.recorrer(doc.id, justificativa.trim());
      setSucesso({ numero: r.numero, instancia: r.instancia });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Falha';
      const traduzido: Record<string, string> = {
        instancias_esgotadas: 'Você já utilizou todas as 4 instâncias de recurso disponíveis.',
        aguardando_resposta: 'Aguarde a resposta da prefeitura antes de recorrer.',
        documento_nao_e_esic: 'Este pedido não é do e-SIC.',
        sem_acesso: 'Você não tem acesso a este pedido.',
        nao_autenticado: 'Sessão expirada. Faça login novamente.',
        justificativa_curta: 'A justificativa precisa de no mínimo 10 caracteres.',
      };
      setErro(traduzido[msg] ?? msg);
    } finally {
      setEnviando(false);
    }
  }

  if (carregando) {
    return (
      <PortalPage titulo="Recurso e-SIC" icone={<FiCornerUpLeft size={26} />} voltarHref="/minhas-solicitacoes">
        <div className="flex justify-center py-16"><Spinner tamanho="lg" /></div>
      </PortalPage>
    );
  }

  if (erro && !doc) {
    return (
      <PainelResultado
        titulo="Pedido não disponível"
        descricao={erro}
        icone={<FiAlertCircle size={32} />}
        tom="aviso"
        acoes={
          <Button variant="outline" onClick={() => navigate('/minhas-solicitacoes')}>
            <FiCornerUpLeft size={14} /> Voltar
          </Button>
        }
      />
    );
  }

  if (sucesso) {
    return (
      <PainelResultado
        titulo="Recurso protocolado"
        descricao={
          <>
            {INSTANCIA_DESCRICAO[sucesso.instancia]?.nome} de recurso · Protocolo{' '}
            <strong className="text-slate-900">{sucesso.numero}</strong>
          </>
        }
        acoes={
          <Button onClick={() => navigate(`/solicitacao/${sucesso.numero}`)}>
            Acompanhar recurso
          </Button>
        }
      >
        <Alert variante="info">
          A autoridade competente analisará seu recurso. Você receberá notificações por e-mail
          a cada movimentação.
        </Alert>
      </PainelResultado>
    );
  }

  if (!doc) return null;

  const instanciaAtual = (doc.campos_customizados?.['instancia_recurso'] as number | undefined) ?? 0;
  const proximaInstancia = instanciaAtual + 1;
  const podeRecorrer = proximaInstancia <= 4;
  const info = INSTANCIA_DESCRICAO[proximaInstancia];

  return (
    <PortalPage
      titulo={podeRecorrer ? `Recurso ${info.nome}` : 'Recursos esgotados'}
      subtitulo={`Pedido original: ${doc.numero} — ${doc.titulo}`}
      icone={<FiCornerUpLeft size={26} />}
      voltarHref={`/solicitacao/${doc.numero}`}
      voltarLabel="Voltar ao pedido"
    >
      {!podeRecorrer ? (
        <SectionCard titulo="Recursos esgotados" icone={<FiAlertCircle size={18} />}>
          <p className="text-sm text-slate-700">
            Você já utilizou todas as 4 instâncias de recurso disponíveis para este pedido.
          </p>
        </SectionCard>
      ) : (
        <div className="space-y-5">
          <Alert variante="info">
            <strong>{info.nome}.</strong> {info.descricao}
          </Alert>

          <form onSubmit={handleSubmit} className="space-y-5">
            {erro && <Alert variante="erro">{erro}</Alert>}

            <SectionCard titulo="Justificativa do recurso" icone={<FiCornerUpLeft size={18} />}>
              <Label htmlFor="just" obrigatorio>Justificativa do recurso</Label>
              <p className="text-xs text-slate-500 mb-2">
                Explique por que você discorda da resposta recebida e o que espera obter no recurso.
              </p>
              <textarea
                id="just"
                value={justificativa}
                onChange={(e) => setJustificativa(e.target.value)}
                rows={8}
                required
                placeholder="Descreva detalhadamente os motivos do seu recurso..."
                className="w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:border-institutional focus:ring-2 focus:ring-institutional resize-y"
              />
            </SectionCard>

            <div className="flex items-center justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => navigate(`/solicitacao/${doc.numero}`)}>
                Cancelar
              </Button>
              <Button type="submit" loading={enviando}>
                <FiSend size={14} /> Protocolar recurso
              </Button>
            </div>
          </form>
        </div>
      )}
    </PortalPage>
  );
}
